import { LitElement, css, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import likeSvg from '../assets/like.svg'
import likeActiveSvg from '../assets/likeActive.svg'
import dislikeSvg from '../assets/dislike.svg'
import dislikeActiveSvg from '../assets/dislikeActive.svg'
import copySvg from '../assets/copy.svg'
import aiSvg from '../assets/ai.svg'
import { SummaryService, type ISummaryService } from '../services/summary-service';
import { stripInlineCitations } from '../services/citations';
import type { AgentSummary } from '../models/AgentSummary';
import './feedback-modal';
import './toast';

@customElement('facs-ai-summary')
export class AISummary extends LitElement {
    @property()
    query = ''

    @property()
    domain = ''

    @property({ attribute: 'trigger-words' })
    triggerWords = '[]'

    @state()
    private summary: AgentSummary | null = null

    // Held alongside the summary it belongs to, so a rating can only ever refer to the answer
    // currently on screen.
    private summaryId: string | null = null

    @state()
    private loading = false

    @state()
    private streamingAnswer = ''

    @state()
    private expanded = false;

    @state()
    private isOverflowing = false;

    @state()
    private modalOpen = false;

    @state()
    private likeActive = false;

    @state()
    private dislikeActive = false;

    private summaryService: ISummaryService = new SummaryService();
    private _abortController: AbortController | null = null;

    private matchesTriggerWords(query: string): boolean {
        let words: string[];
        try {
            words = JSON.parse(this.triggerWords || '[]');
        } catch {
            words = [];
        }
        if (words.length === 0) return true;
        const queryWords = query.split(/\s+/).map(w => w.replace(/[^\w]/g, ''));
        return queryWords.some(qw =>
            words.some(tw => qw.toLowerCase() === tw.toLowerCase())
        );
    }

    updated(changedProps: Map<string, any>) {
        if (changedProps.has('query') && this.query) {
            if (this.matchesTriggerWords(this.query)) {
                this.hidden = false;
                this.fetchSummary();
            } else {
                this.hidden = true;
                this.summary = null;
                this.loading = false;
            }
        }

        if (changedProps.has('summary') && this.summary) {
            this.updateComplete.then(() => {
                const answer = this.renderRoot.querySelector('#answer') as HTMLElement;
                if (!answer) return;

                this.isOverflowing = answer.scrollHeight > answer.clientHeight;
            });
        }
    }

    async fetchSummary() {
        this._abortController?.abort();
        this._abortController = new AbortController();

        this.loading = true;
        this.streamingAnswer = '';
        this.summary = null;
        this.summaryId = null;
        this.likeActive = false;
        this.dislikeActive = false;

        await this.summaryService.generateSummaryStream(
            this.query,
            this.domain,
            (chunk) => {
                this.loading = false;
                this.streamingAnswer += chunk;
            },
            (summary, summaryId) => {
                this.streamingAnswer = '';
                this.summary = summary;
                this.summaryId = summaryId;
            },
            (_error) => {
                this.loading = false;
                this.streamingAnswer = '';
                // summary stays null → "Something went wrong" state
            },
            this._abortController.signal
        );

        this.loading = false;
    }

    copyText() {
        const text = this.summary?.answer ?? '';
        navigator.clipboard.writeText(text);
        const toast = this.renderRoot.querySelector('#copy-toast') as any;
        toast?.show(3000);
    }

    private openFeedbackModal() {
        this.modalOpen = true;
    }

    private handleModalClose() {
        this.modalOpen = false;
    }

    private handleModalSubmit(e: CustomEvent) {
        this.modalOpen = false;
        void this.summaryService.submitFeedback(this.summaryId, e.detail?.feedback, this.domain);
        const toast = this.renderRoot.querySelector('#feedback-toast') as any;
        toast?.show(3000);
    }

    // A rating can be changed but not cleared. Clearing it emitted no event, which left a
    // retracted rating indistinguishable from a standing one; ignoring the second click keeps the
    // recorded rating and the button state in agreement.
    private handleLike() {
        if (this.likeActive) return;

        this.likeActive = true;
        this.dislikeActive = false;
        void this.summaryService.submitRating(this.summaryId, 'Up', this.domain);
    }

    private handleDislike() {
        if (this.dislikeActive) return;

        this.dislikeActive = true;
        this.likeActive = false;
        void this.summaryService.submitRating(this.summaryId, 'Down', this.domain);
        this.openFeedbackModal();
    }

    connectedCallback() {
        super.connectedCallback();

        // Immediately sync component state with current URL query parameter on mount
        this.syncQueryFromUrl();

        // Listen for browser navigation (back/forward)
        window.addEventListener('popstate', this.syncQueryFromUrl);

        // Listen for custom URL changes triggered by pushState/replaceState
        window.addEventListener('urlchange', this.syncQueryFromUrl);

        // ---- Patch history API to emit a custom event on URL changes ----

        // Save original pushState and replaceState methods
        const _pushState = history.pushState;
        const _replaceState = history.replaceState;

        // Wrap pushState so that it dispatches a 'urlchange' event whenever called
        history.pushState = function (...args) {
            const result = _pushState.apply(this, args);
            window.dispatchEvent(new Event('urlchange'));
            return result;
        };

        // Wrap replaceState similarly
        history.replaceState = function (...args) {
            const result = _replaceState.apply(this, args);
            window.dispatchEvent(new Event('urlchange'));
            return result;
        };
    }

    disconnectedCallback() {
        this._abortController?.abort();
        window.removeEventListener('popstate', this.syncQueryFromUrl);
        window.removeEventListener('urlchange', this.syncQueryFromUrl);
        super.disconnectedCallback();
    }

    private searchParam = "searchTerm";

    // Sync the component's `query` property with the current URL
    private syncQueryFromUrl = () => {
        const params = new URLSearchParams(window.location.search);
        const query = params.get(this.searchParam);

        // Only update component state if query is non-empty and different from current
        if (query && query !== this.query) {
            this.query = query;
        }
    };

    private disclaimerText = "Answers are generated by AI. Please verify information by referencing provided sources for each answer.";

    render() {
        if (!this.query && !this.loading && !this.streamingAnswer && !this.summary) {
            return html``;
        }

        return html`
        <div class="summary">
            <facs-card>
                ${this.loading
                ? html`
                        <p slot="title" class="loading-text">
                            Searching<span class="ellipsis"></span>
                        </p>
                    `
                : this.streamingAnswer
                    ? html`
                            <p slot="title">${this.query}</p>
                            <div class="disclaimer-container">
                                <img src="${aiSvg}" class="icon" alt="" />
                                <p class="disclaimer">${this.disclaimerText}</p>
                            </div>
                            <div class="answer-wrapper">
                                <div id="answer" class="expanded">${stripInlineCitations(this.streamingAnswer)}</div>
                            </div>
                        `
                : this.summary
                    ? html`
                            <p slot="title">
                                ${this.query}
                            </p>
                            <div class="disclaimer-container">
                             <img src="${aiSvg}" class="icon" alt="" />
                             <p class="disclaimer">
                                ${this.disclaimerText}
                             </p>
                            </div>
                            <div class="answer-wrapper">
                                <div
                                    id="answer"
                                    class=${this.expanded ? 'expanded' : 'collapsed'}
                                >
                                    ${this.summary.answer}
                                </div>

                                ${this.isOverflowing
                            ? html`
                                        <button
                                            class="show-more"
                                            @click=${() => (this.expanded = !this.expanded)}
                                        >
                                            ${this.expanded ? 'Show Less' : 'Show More'}
                                        </button>
                                      `
                            : null}
                            </div>
                            <div class="buttons">
                             <button aria-label="Like" aria-pressed=${this.likeActive} @click=${this.handleLike}>
                                 ${this.likeActive
                                    ? html`<img src="${likeActiveSvg}" class="icon" alt="thumbs up" />`
                                    : html`<img src="${likeSvg}" class="icon" alt="thumbs up" />`
                                  }                            
                            </button>

                             <button aria-label="Dislike" aria-pressed=${this.dislikeActive} @click=${this.handleDislike}>
                                ${this.dislikeActive
                                    ? html`<img src="${dislikeActiveSvg}" class="icon" alt="thumbs down" />`
                                     : html`<img src="${dislikeSvg}" class="icon" alt="thumbs down" />`
                                }
                            </button>

                             <button class="icon-btn" @click=${this.copyText} aria-label="Copy" id="copy-btn">
                                <img src="${copySvg}" class="icon" alt="copy" />
                             </button>
                            </div>
                          `
                    : html`
                          <p slot="title">
                                Something went wrong...
                            </p>
                          `
            }
            </facs-card>
            ${(this.summary?.sources?.length ?? -1) > 0
                ? html`<facs-sources .sources=${this.summary?.sources}></facs-sources>`
                : null
            }
        </div>

        <facs-feedback-modal
            .open=${this.modalOpen}
            @modal-close=${this.handleModalClose}
            @modal-submit=${this.handleModalSubmit}
        ></facs-feedback-modal>

        <facs-toast id="feedback-toast"></facs-toast>
        <facs-toast id="copy-toast" message="Text copied to clipboard" variant="neutral"></facs-toast>
        `
    }

    static styles = css`
        .summary {
            display: flex;
            flex-direction: column;
            border: 0.125rem solid #F3F6F7;
            border-left: none;
            border-radius: 1rem;
            overflow: hidden;
            box-shadow: 0 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.15);
        }

        .loading-text {
            color: #B1B1B1;
            font-size: 1.125rem;
            font-weight: 400;
        }

        .ellipsis::after {
            content: '';
            animation: ellipsis 1.5s steps(4, end) infinite;
        }

        @keyframes ellipsis {
            0%  { content: ''; }
            25% { content: '.'; }
            50% { content: '..'; }
            75% { content: '...'; }
        }

        .disclaimer-container {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 0.5rem;

            .disclaimer {
               font-size: 0.75rem;
               color: #555;
               line-height: 1.25rem;
            }
        }

        .icon {
            height: 1.5rem;
        }

        .buttons {
            display: flex;
            gap: 0.75rem;
            justify-content: right;
            margin-top: 0.75rem;
        }

        button {
            all: unset;
            cursor: pointer;
        }

        @media (max-width: 840px) {
            .disclaimer {
                margin: 0.5rem 0 1rem 0;
            }

            .buttons {
                margin-top: 1rem;
            }

            .show-more {
                bottom: -5.125rem !important;
            }
        }

        .answer-wrapper {
            position: relative;
        }

        #answer {
            max-height: 12rem;
            overflow: hidden;
        }

        #answer.expanded {
            max-height: none;
        }

        .show-more {
            position: absolute;
            bottom: -4.875rem;
            left: 50%;
            transform: translateX(-50%);
            z-index: 2;

            padding: 0.5rem 1rem;
            font-size: 0.875rem;
            font-weight: 600;
            line-height: 1.125rem;
            border-radius: 6.25rem;
            background: white;
            border: 0.125rem solid #0f2144;
        }
    `;
}